import { useState } from "react";
import axios from "axios";
import { Printer, ShieldCheck, TrendingUp, Coins, Landmark, Scale, ArrowRight, Mail, Phone, Handshake, CheckCircle2 } from "lucide-react";

const GOLD = "#C8A24B";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROFESSIONI = ["Commercialista", "Avvocato", "Notaio", "Consulente finanziario", "Consulente del lavoro", "Altro professionista"];

const Table = ({ headers, rows, highlightLast }) => (
  <table className="w-full border-collapse text-sm my-4">
    <thead>
      <tr>
        {headers.map((h, i) => (
          <th key={i} className={`text-left p-3 bg-[#1a1a1a] text-white font-semibold ${i === 0 ? "" : "text-right"}`}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => {
        const isLast = highlightLast && i === rows.length - 1;
        return (
          <tr key={i} className={isLast ? "bg-[#faf3e0] font-bold" : i % 2 ? "bg-slate-50" : "bg-white"}>
            {r.map((c, j) => (
              <td key={j} className={`p-3 border border-slate-200 ${j === 0 ? "text-left" : "text-right"} ${isLast ? "text-[#8a6d1f]" : "text-slate-700"}`}>{c}</td>
            ))}
          </tr>
        );
      })}
    </tbody>
  </table>
);

const Section = ({ n, title, sub, children }) => (
  <section className="mb-14 break-inside-avoid">
    <div className="flex items-baseline gap-3 border-b-2 border-[#C8A24B] pb-2 mb-5">
      {n && <span className="font-head font-extrabold text-2xl text-[#C8A24B]">{n}</span>}
      <div>
        <h2 className="font-head font-bold text-2xl text-slate-900 tracking-tight">{title}</h2>
        {sub && <p className="text-slate-500 text-sm mt-0.5">{sub}</p>}
      </div>
    </div>
    {children}
  </section>
);

const Callout = ({ children }) => (
  <div className="border-l-4 border-[#C8A24B] bg-[#faf7ef] p-4 my-4 text-slate-700 italic">{children}</div>
);

const Pill = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 bg-[#1a1a1a] text-white px-4 py-3 text-sm font-medium">
    <Icon size={16} style={{ color: GOLD }} /> {children}
  </div>
);

function PartnerForm() {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", professione: PROFESSIONI[0], messaggio: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        tipo: "collaboratore",
        nome: form.nome,
        email: form.email,
        telefono: form.telefono,
        messaggio: `[Alleato · ${form.professione}] ${form.messaggio}`.trim(),
      });
      setDone(true);
    } catch {
      alert("Errore nell'invio. Riprova o scrivi a alassane-diallo@libero.it");
    } finally { setSending(false); }
  };

  const input = "w-full bg-black/40 border border-zinc-700 rounded-none text-white placeholder-zinc-500 px-4 py-3 focus:border-[#C8A24B] focus:outline-none focus:ring-1 focus:ring-[#C8A24B] transition-colors";

  if (done) {
    return (
      <div className="text-center py-6" data-testid="partner-form-success">
        <CheckCircle2 size={38} className="mx-auto mb-3" style={{ color: GOLD }} />
        <p className="font-head font-bold text-xl text-white">Candidatura ricevuta ✅</p>
        <p className="text-zinc-400 mt-1">Ti ricontatto personalmente per attivare la collaborazione.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid md:grid-cols-2 gap-3" data-testid="partner-form">
      <input className={input} placeholder="Nome e cognome *" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="partner-nome" />
      <input className={input} type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="partner-email" />
      <input className={input} placeholder="Telefono / WhatsApp" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="partner-telefono" />
      <select className={input} value={form.professione} onChange={(e) => setForm({ ...form, professione: e.target.value })} data-testid="partner-professione">
        {PROFESSIONI.map((p) => <option key={p} value={p} className="bg-zinc-900">{p}</option>)}
      </select>
      <textarea className={`${input} md:col-span-2 min-h-[80px]`} placeholder="Note (facoltativo): studio, città, tipologia di clientela..." value={form.messaggio} onChange={(e) => setForm({ ...form, messaggio: e.target.value })} data-testid="partner-messaggio" />
      <button type="submit" disabled={sending} data-testid="partner-submit"
        className="md:col-span-2 flex items-center justify-center gap-2 bg-[#C8A24B] text-black font-bold py-3.5 hover:brightness-95 disabled:opacity-50 transition">
        {sending ? "Invio..." : "DIVENTA ALLEATO"} <ArrowRight size={18} />
      </button>
    </form>
  );
}

export default function Presentazione() {
  return (
    <div className="bg-slate-100 min-h-screen py-8 print:py-0 print:bg-white">
      <style>{`@media print {.no-print{display:none!important;} body{-webkit-print-color-adjust:exact;print-color-adjust:exact;} .doc{box-shadow:none!important;margin:0!important;}}`}</style>

      <div className="no-print max-w-[820px] mx-auto mb-4 flex justify-end px-4">
        <button onClick={() => window.print()} data-testid="print-button"
          className="flex items-center gap-2 bg-[#C8A24B] text-black font-semibold px-5 py-2.5 hover:brightness-95 transition">
          <Printer size={17} /> Stampa / Salva in PDF
        </button>
      </div>

      <div className="doc max-w-[820px] mx-auto bg-white shadow-xl">
        {/* Cover */}
        <div className="bg-[#111111] text-white px-10 py-16 text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="p-1.5" style={{ background: GOLD }}><ShieldCheck size={18} className="text-black" /></div>
            <span className="font-head font-extrabold tracking-tight">ALEX GOLD</span>
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">Protezione Patrimonio</span>
          </div>
          <p className="text-xs font-mono uppercase tracking-[0.3em]" style={{ color: GOLD }}>Documento per Professionisti · Commercialisti · Avvocati · Notai · Consulenti</p>
          <h1 className="font-head font-extrabold text-3xl md:text-4xl mt-4 leading-tight">
            L'Oro Fisico come Strumento di<br /><span style={{ color: GOLD }}>Tesoreria, TFR/TFM e Successione</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto text-sm">
            Riforma TFR e Previdenza Complementare 2026 e strategie di protezione patrimoniale d'impresa con l'oro fisico da investimento.
          </p>
        </div>

        <div className="px-10 py-12">
          {/* PARTE 1 */}
          <p className="font-head font-extrabold text-sm uppercase tracking-[0.2em] text-[#C8A24B] mb-6">Parte 1 · Riforma TFR e Previdenza 2026</p>

          <Section n="1" title="Obiettivo della riforma" sub="Legge di Bilancio 2026 — sintesi per aziende">
            <ul className="space-y-2 text-slate-700 list-disc pl-5">
              <li>Spingere l'adesione ai fondi pensione</li>
              <li>Rafforzamento del meccanismo di silenzio-assenso</li>
              <li>Riduzione del TFR mantenuto in azienda → minore autofinanziamento</li>
            </ul>
          </Section>

          <Section n="2" title="Nuovi assunti" sub="Dal 1° luglio 2026">
            <p className="text-slate-700 mb-2">Restano <strong>6 mesi</strong> per scegliere la destinazione del TFR, ma il silenzio diventa <strong>vincolante</strong>.</p>
            <Callout>
              Se il dipendente <strong>NON sceglie</strong> → adesione automatica al fondo pensione di categoria (CCNL), con obbligo aziendale di versare TFR + contributo datoriale previsto dal CCNL.
            </Callout>
          </Section>

          <Section n="3" title="Dipendenti già in forza" sub="Finestra straordinaria «ponte»: 1° luglio – 31 dicembre 2026">
            <p className="text-slate-700 mb-2">Il lavoratore deve <strong>confermare per iscritto</strong> di voler mantenere il TFR in azienda.</p>
            <p className="text-slate-700">Se non lo fa → dal <strong>1° gennaio 2027</strong> il TFR è trasferito automaticamente al fondo pensione di categoria. Anche qui: <strong>silenzio = adesione automatica</strong>.</p>
          </Section>

          <Section n="4" title="Adempimenti obbligatori per le aziende">
            <ol className="space-y-2 text-slate-700 list-decimal pl-5">
              <li>Identificare il fondo pensione di categoria</li>
              <li>Verificare le aliquote contributive aggiornate</li>
              <li>Inviare solleciti formali ai dipendenti «silenti»</li>
              <li>Usare solo il Modello <strong>TFR2</strong> aggiornato</li>
              <li>Tracciare scadenze e risposte (rischio contenziosi)</li>
            </ol>
            <Callout>Vale per <strong>TUTTE le aziende</strong>, anche con 1 solo dipendente.</Callout>
          </Section>

          <Section n="5" title="Fondo Tesoreria INPS" sub="Fine di una storica fonte di autofinanziamento">
            <Table
              headers={["Periodo", "Soglia (dipendenti medi)"]}
              rows={[["Fino al 2025", "Obbligo solo se ≥ 50 dip. (alla nascita o al 31/12/2006)"], ["2026 – 2027", "≥ 60 dipendenti medi"], ["2028 – 2031", "≥ 50 dipendenti medi"], ["Dal 2032", "≥ 40 dipendenti medi"]]}
            />
            <p className="text-slate-700">Dal 2026 conta la <strong>media annuale</strong> dei dipendenti: l'obbligo può scattare anche anni dopo la costituzione. Il TFR dei dipendenti non in previdenza va all'INPS, <strong>non resta in azienda</strong>.</p>
          </Section>

          <Section n="6" title="Impatto concreto e aspetti positivi">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-slate-900 mb-2">Impatto per le aziende</p>
                <ul className="space-y-1.5 text-slate-700 list-disc pl-5 text-sm">
                  <li>Meno liquidità disponibile</li>
                  <li>Perdita del TFR come leva finanziaria</li>
                  <li>Revisione di cash flow, budget 2026, processi HR e payroll</li>
                  <li>Necessità di adeguarsi in tempi rapidi</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-2">Aspetti positivi (lavoratori/welfare)</p>
                <ul className="space-y-1.5 text-slate-700 list-disc pl-5 text-sm">
                  <li>Deducibilità previdenza: da €5.164,57 → €5.300</li>
                  <li>Maggiore copertura pensionistica futura</li>
                  <li>Spinta strutturale alla previdenza complementare</li>
                </ul>
              </div>
            </div>
            <Callout>Dal 2026 il TFR smette di essere una riserva aziendale automatica. Il silenzio dei lavoratori non tutela più l'azienda, ma la <strong>obbliga a versare</strong> TFR e contributi verso fondi pensione o INPS.</Callout>
          </Section>

          {/* PARTE 2 */}
          <p className="font-head font-extrabold text-sm uppercase tracking-[0.2em] text-[#C8A24B] mb-6 mt-4">Parte 2 · L'Oro Fisico per le PMI</p>

          <Section title="Premessa strategica">
            <p className="text-slate-700 mb-4">In un contesto di inflazione strutturale, volatilità dei mercati e pressione fiscale, le PMI devono ottimizzare la gestione delle passività (TFR e TFM) e proteggere liquidità e patrimonio. L'oro fisico da investimento è uno strumento <strong>reale, liquido e fiscalmente efficiente</strong>.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Pill icon={ShieldCheck}>Tutela potere d'acquisto</Pill>
              <Pill icon={TrendingUp}>Pianificazione finanziaria</Pill>
              <Pill icon={Coins}>Diversificazione</Pill>
              <Pill icon={Scale}>Passaggio generazionale</Pill>
            </div>
          </Section>

          <Section title="TFR in Oro — Vantaggi per le PMI" sub="Trasformare una passività monetaria in accantonamento reale indicizzato all'inflazione">
            <Table
              headers={["Aspetto", "Vantaggio per l'impresa"]}
              rows={[
                ["Protezione inflazione", "Preserva il potere d'acquisto: rivalutazione ben superiore al 75% + 1,5% di legge"],
                ["Rischio ridotto", "Non dipende da rendimenti di mercato o tassi; nessun emittente a rischio default"],
                ["Coerenza attivo/passivo", "Passività future coperte da un bene reale → miglior rating bancario"],
                ["Liquidabilità", "Liquidabile in 6/7 giorni, in parte o totalmente"],
                ["Trasparenza", "Quotazione internazionale, nessun costo nascosto"],
                ["Flessibilità", "Si può accantonare anche solo il 20/30% del TFR e creare una riserva di valore"],
              ]}
            />
            <Callout>Per la PMI il TFR non è più solo un costo «che cresce», ma una <strong>riserva reale di valore</strong>. L'oro accantonato resta liquidabile in ogni momento; l'orizzonte a 10 anni è solo esemplificativo.</Callout>
          </Section>

          <Section title="Esempio — TFR e copertura dell'obbligo legale" sub="Ipotesi prudenziali: 10 dipendenti · TFR annuo tot. 22.000 € · 30% in oro · orizzonte 10 anni · oro 31→122 €/g (+293,55%, dati 2016-2026)">
            <Table
              headers={["Voce", "Dato"]}
              rows={[
                ["TFR totale maturato (10 anni)", "220.000 €"],
                ["Rivalutazione legale (1,5% + inflazione)", "≈ +75.000 €"],
                ["TFR totale da onorare per legge", "≈ 295.000 €"],
                ["Quota TFR in oro (30%)", "66.000 €"],
                ["Quota TFR usata operativamente (70%)", "154.000 €"],
                ["Valore finale oro (dati storici)", "≈ 260.000 €"],
                ["Copertura del TFR totale grazie all'oro", "≈ 88%"],
              ]}
              highlightLast
            />
            <Callout>Accantonando solo il <strong>30%</strong> del TFR in oro, l'azienda copre storicamente quasi l'intero obbligo di TFR rivalutato, pur usando il 70% per l'attività caratteristica.</Callout>
          </Section>

          <Section title="TFM in Oro — Vantaggi per PMI e Imprenditore" sub="Il Trattamento di Fine Mandato come strumento di tutela patrimoniale e pianificazione fiscale">
            <Table
              headers={["Aspetto", "Vantaggio"]}
              rows={[
                ["Stabilità nel tempo", "Riduce l'erosione del valore del TFM"],
                ["Separazione patrimoniale", "Accantonamento dedicato e tracciabile"],
                ["Efficienza fiscale", "Deducibile come costo, tassazione finale separata per il socio-amministratore"],
                ["Tutela amministratore", "Protezione del compenso differito"],
                ["Diversificazione", "Non correlato agli strumenti finanziari"],
              ]}
            />
          </Section>

          <Section title="TFM su 10 anni — Effetto fiscale e confronto" sub="Ipotesi: TFM 100.000 €/anno · 10 anni · IRES 24% + IRAP 3,9% · tass. separata 30% · dividendi 26%">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-slate-900 text-sm mb-1">Effetto per l'azienda</p>
                <Table headers={["Voce", "Importo"]} rows={[["TFM 10 anni", "1.000.000 €"], ["Risparmio fiscale (27,9%)", "279.000 €"], ["Costo netto reale", "721.000 €"]]} />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm mb-1">Netto al socio: TFM vs Dividendi</p>
                <Table headers={["Modalità", "Netto al socio"]} rows={[["TFM (tass. separata)", "700.000 €"], ["Dividendi (doppia tass.)", "≈ 533.540 €"], ["Extra risparmio con TFM", "≈ +166.460 €"]]} highlightLast />
              </div>
            </div>
            <Table headers={["Risparmio combinato (azienda + socio)", "Importo"]} rows={[["Risparmio azienda (deduzione)", "279.000 €"], ["Extra netto per il socio", "166.460 €"], ["Totale valore generato", "≈ 445.460 €"]]} highlightLast />
          </Section>

          <Section title="E se accantono il TFM in oro?" sub="TFM 1.000.000 € su 10 anni · rivalutazione storica oro +293,55%">
            <Table
              headers={["Voce", "Importo"]}
              rows={[
                ["TFM complessivo accantonato", "1.000.000 €"],
                ["Valore finale con oro (dati storici)", "≈ 3.935.500 €"],
                ["Plusvalenza generata", "≈ +2.935.500 €"],
                ["Tassazione separata (30% stima prudenziale)", "≈ -1.180.650 €"],
                ["Netto finale al socio amministratore", "≈ 2.754.850 €"],
              ]}
              highlightLast
            />
            <Callout>Il TFM in oro unisce <strong>efficienza fiscale immediata</strong> e capacità storica di preservare e accrescere il valore nel lungo periodo.</Callout>
          </Section>

          <Section title="Tesoreria aziendale in oro" sub="Perché affiancare l'oro alla liquidità">
            <p className="text-slate-700 mb-3">La liquidità aziendale non rende, perde valore reale ed è esposta a rischio bancario e inflattivo. L'oro diventa una <strong>riserva strategica complementare</strong> (non alternativa) alla liquidità operativa.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <Pill icon={ShieldCheck}>Potere d'acquisto</Pill>
              <Pill icon={Coins}>Liquidabilità</Pill>
              <Pill icon={Landmark}>Fuori dal sistema bancario</Pill>
              <Pill icon={TrendingUp}>Copertura shock macro</Pill>
            </div>
            <p className="text-slate-500 text-sm mb-1">Caso: PMI con tesoreria 1.500.000 € — 1.000.000 € operativa, 500.000 € strutturalmente inattiva.</p>
            <Table
              headers={["Gestione della quota inattiva (500.000 €)", "Valore dopo 10 anni"]}
              rows={[["Conto / deposito 3%", "≈ 671.900 €"], ["Oro fisico (dati storici)", "≈ 1.967.700 €"], ["Extra valore potenziale", "≈ +1.295.800 €"]]}
              highlightLast
            />
            <Callout>La parte di tesoreria strutturalmente inutilizzata può diventare una riserva in oro che preserva e accresce il valore, migliorando solidità aziendale, percezione bancaria e resilienza agli shock — <strong>senza compromettere l'operatività</strong>.</Callout>
          </Section>

          <Section title="Oro e successione patrimoniale" sub="Trasmissione efficiente e ordinata del patrimonio">
            <ol className="space-y-1.5 text-slate-700 list-decimal pl-5 text-sm mb-3">
              <li>Non soggetto a capital gain in caso di successione</li>
              <li>Trasferito agli eredi come bene materiale (merce)</li>
              <li>Facilità di suddivisione tra eredi</li>
              <li>Bene universalmente riconosciuto e valorizzabile</li>
              <li>Continuità patrimoniale senza discontinuità finanziarie</li>
            </ol>
            <p className="text-slate-500 text-sm mb-1">Esempio: 5.000 €/mese × 10 anni (600.000 € versati) · rendimento come oro storico +293,55% → valore finale lordo ≈ 2.361.300 €.</p>
            <Table
              headers={["Scenario", "Valore agli eredi"]}
              rows={[["Oro fisico (nessun capital gain)", "2.361.300 €"], ["Prodotto finanziario (26% su plusvalenza)", "≈ 1.903.400 €"], ["Più valore agli eredi con l'oro", "≈ +457.900 €"]]}
              highlightLast
            />
            <Callout>Con l'oro fisico gli eredi ricevono <strong>circa 458.000 € in più</strong> rispetto a un prodotto finanziario a parità di rendimento lordo, grazie all'assenza di capital gain in successione.</Callout>
          </Section>

          <Section title="Conclusione">
            <div className="grid md:grid-cols-3 gap-2 mb-4">
              <Pill icon={Coins}>Gestione evoluta di TFR e TFM</Pill>
              <Pill icon={Landmark}>Protezione della tesoreria</Pill>
              <Pill icon={Scale}>Asset per diversificazione e successione</Pill>
            </div>
            <p className="font-head font-bold text-xl text-slate-900">L'oro non è una scommessa finanziaria, ma una scelta di solidità, visione e responsabilità patrimoniale.</p>
          </Section>

          {/* CTA Partner */}
          <div className="bg-[#111111] -mx-10 px-10 py-12 mt-4 break-inside-avoid" data-testid="cta-partner">
            <div className="flex items-center gap-2 mb-3">
              <Handshake size={20} style={{ color: GOLD }} />
              <span className="text-xs font-mono uppercase tracking-[0.25em]" style={{ color: GOLD }}>Collabora con me</span>
            </div>
            <h2 className="font-head font-extrabold text-2xl md:text-3xl text-white leading-tight">
              Diventa <span style={{ color: GOLD }}>alleato</span>
            </h2>
            <p className="text-zinc-400 mt-3 mb-6 max-w-2xl text-sm">
              Sei un commercialista, avvocato, notaio o consulente? Diventa alleato di ALEX GOLD: offri ai tuoi clienti una soluzione concreta di protezione patrimoniale con l'oro fisico e guadagna sulle segnalazioni. Lascia i tuoi dati: ti ricontatto personalmente.
            </p>
            <PartnerForm />
          </div>

          {/* Disclaimer + contatti */}
          <div className="border-t border-slate-200 pt-6 mt-8">
            <p className="text-[11px] text-slate-400 leading-relaxed mb-4">
              I dati si basano sull'andamento storico dell'oro (2016-2026) e non costituiscono garanzia di rendimento futuro. L'oro è utilizzato come strumento di copertura patrimoniale, non come prodotto speculativo. I vantaggi fiscali derivano dal trattamento dell'oro fisico come bene materiale. Documento a scopo informativo, non costituisce consulenza finanziaria, fiscale o legale.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-700">
              <span className="font-head font-extrabold">ALEX GOLD · Protezione Patrimonio</span>
              <span className="flex items-center gap-2"><Mail size={14} style={{ color: GOLD }} /> alassane-diallo@libero.it</span>
              <span className="flex items-center gap-2"><Phone size={14} style={{ color: GOLD }} /> +39 349 717 3896</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
